import React, { Component } from "react";
import MiniPalette from "./MiniPalette";
import { withStyles } from "@material-ui/styles";
import { Link } from 'react-router-dom';
import styles from './styles/PaletteListStyles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import MetaTags from './components/MetaTags';
import GlobalNav from './components/GlobalNav';
import AutoAwesomeIcon from '@material-ui/icons/Stars';

class PaletteList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      openDeleteDialog: false,
      deletingId: ""
    };
    this.openDialog = this.openDialog.bind(this);
    this.closeDialog = this.closeDialog.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.goToPalette = this.goToPalette.bind(this);
  }

  openDialog(id) {
    this.setState({ openDeleteDialog: true, deletingId: id });
  }

  closeDialog() {
    this.setState({ openDeleteDialog: false, deletingId: "" });
  }

  goToPalette(id) {
    this.props.history.push(`/palette/${id}`);
  }

  handleDelete() {
    this.props.deletePalette(this.state.deletingId);
    this.closeDialog();
  }
  
  openInGenerator(palette, isUser) {
    const { history } = this.props;
    if (!history) return;
    if (isUser) {
      // Carry full metadata so the palette can be edited and re-saved.
      history.push({ pathname: '/generate', state: { palette: { ...palette, isUser: true } } });
    } else {
      // Presets: load colors only — never mutate the preset.
      const hex = (palette.colors || [])
        .map(c => (c.color || c.hex || '').replace('#', ''))
        .filter(Boolean)
        .join('-');
      history.push(`/generate/${hex}`);
    }
  }

  renderGrid(list, opts = {}) {
    const { classes } = this.props;
    return (
      <div className={classes.palettes}>
        {list.map(palette => (
          <div key={palette.id} className={classes.paletteWrapper}>
            <MiniPalette
              {...palette}
              id={palette.id}
              deletable={opts.deletable}
              editable={opts.editable}
              handleClick={this.goToPalette}
              handleDelete={this.openDialog}
              onOpenInGenerator={() => this.openInGenerator(palette, !!opts.isUser)}
            />
          </div>
        ))}
      </div>
    );
  }

  // Decorative wall of palette thumbnails for the hero (built from presets).
  renderHeroArt(seedPalettes) {
    const { classes } = this.props;
    const thumbs = seedPalettes.slice(0, 15);
    const feature = seedPalettes[0];
    return (
      <div className={classes.heroArt} aria-hidden="true">
        <div className={classes.heroGrid}>
          {thumbs.map(p => (
            <div className={classes.thumb} key={`thumb-${p.id}`}>
              {(p.colors || []).slice(0, 6).map((c, i) => (
                <div key={i} style={{ flex: 1, backgroundColor: c.color }} />
              ))}
            </div>
          ))}
        </div>
        {feature && (
          <div className={classes.heroFeature}>
            {(feature.colors || []).slice(0, 5).map((c, i) => (
              <div key={i} style={{ flex: 1, backgroundColor: c.color }} />
            ))}
          </div>
        )}
      </div>
    );
  }

  render() {
    const {
      userPalettes = [],
      seedPalettes = [],
      projects = [],
      collections = [],
      classes,
    } = this.props;
    const { openDeleteDialog } = this.state;
    
    return (
      <>
        <MetaTags
          title="Color Palettes - Custom Color"
          description="Browse and manage your color palettes. Create beautiful, accessible color schemes with professional tools and WCAG compliance checking."
          keywords="color palettes, color schemes, palette library, color collection, design palettes, accessible colors"
          url={window.location.href}
        />
        <div className={classes.root}>
        <GlobalNav />
        <div className={classes.container}>
          {/* Hero Section */}
          <div className={classes.hero}>
            <div className={classes.heroContent}>
              <h1 className={classes.heroTitle}>
                The fastest way to build color palettes
              </h1>
              <p className={classes.heroSubtitle}>
                Generate, fine-tune, and save beautiful color schemes for your designs —
                then export them anywhere you work.
              </p>
              <div className={classes.heroButtons}>
                <Button
                  variant="contained"
                  component={Link}
                  to="/generate"
                  className={classes.primaryButton}
                  startIcon={<AutoAwesomeIcon />}
                >
                  Start the Generator
                </Button>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/explore"
                  className={classes.secondaryButton}
                >
                  Explore palettes
                </Button>
              </div>
            </div>
            {this.renderHeroArt(seedPalettes)}
          </div>

          <div className={classes.trusted}>Built for designers and developers</div>

          {/* All palette sections live below the hero. */}
          <div>

          {/* My Palettes (user-created, deletable) */}
          {userPalettes.length > 0 && (
            <div className={classes.palettesSection}>
              <h2 className={classes.sectionTitle}>My Palettes</h2>
              {this.renderGrid(userPalettes, { deletable: true, editable: true, isUser: true })}
            </div>
          )}

          {/* Projects: palettes grouped by the project they belong to */}
          {projects.map(proj => {
            const inProject = userPalettes.filter(p => (p.projectIds || []).includes(proj.id));
            if (inProject.length === 0) return null;
            return (
              <div className={classes.palettesSection} key={proj.id}>
                <h2 className={classes.sectionTitle}>Project · {proj.name}</h2>
                {this.renderGrid(inProject, { deletable: true, editable: true, isUser: true })}
              </div>
            );
          })}

          {/* Collections: only rendered when at least one collection exists */}
          {collections.length > 0 &&
            collections.map(col => {
              const inCollection = userPalettes.filter(p =>
                (p.collectionIds || []).includes(col.id)
              );
              return (
                <div className={classes.palettesSection} key={col.id}>
                  <h2 className={classes.sectionTitle}>Collection · {col.name}</h2>
                  {inCollection.length > 0 ? (
                    this.renderGrid(inCollection, { deletable: true, editable: true, isUser: true })
                  ) : (
                    <p className={classes.emptyState}>No palettes in this collection yet.</p>
                  )}
                </div>
              );
            })}

          {/* Preset palettes (built-in, not editable or deletable) */}
          <div className={classes.palettesSection}>
            <h2 className={classes.sectionTitle}>Preset Palettes</h2>
            {this.renderGrid(seedPalettes, { deletable: false, editable: false, isUser: false })}
          </div>
          </div>
        </div>

        <Dialog
          open={openDeleteDialog}
          aria-labelledby="delete-dialog-title"
          onClose={this.closeDialog}
          className={classes.dialog}
        >
          <DialogTitle id="delete-dialog-title">Delete This Palette?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this palette? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDialog} color="primary">
              Cancel
            </Button>
            <Button onClick={this.handleDelete} color="secondary">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        </div>
      </>
    );
  }
}
export default withStyles(styles)(PaletteList);
